import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePlanner, UsePlannerOptions } from './usePlanner';
import { useRouteRisk } from './useRouteRisk';
import { useRouteAssets } from './useRouteAssets';
import { useRouteEvents } from './useRouteEvents';
import { useRouteWeather } from './useRouteWeather';
import { makeWaypointId } from './plannerReducer';
import { ManeuverStep, RouteAsset, RouteEvent, RouteRiskSegment, Waypoint } from './types';

export interface RoutePlannerControllerOptions extends UsePlannerOptions {
  /** Planning mode: map clicks add waypoints and shortcuts are live. */
  active: boolean;
  /** Esc leaves planning mode. */
  onExit?: () => void;
  /** Late-bound accessor for the host's mapbox-gl instance. */
  getMap?: () => any;
  /** Changing this reloads the planner from `initialWaypoints` and clears history. */
  sessionKey?: string | number | null;
}

export function waypointFromCoords(
  longitude: number,
  latitude: number,
  label?: string,
  source: Waypoint['source'] = 'mission',
  notes?: string,
): Waypoint {
  return {
    id: makeWaypointId(),
    longitude,
    latitude,
    label,
    labelStatus: label ? 'done' : 'idle',
    source,
    notes,
  };
}

/**
 * Planner state plus the interaction glue a host map needs: click-to-add,
 * undo/redo shortcuts, Esc to exit, and camera helpers.
 */
export function useRoutePlannerController({
  active,
  onExit,
  getMap,
  sessionKey,
  ...plannerOptions
}: RoutePlannerControllerOptions) {
  const planner = usePlanner(plannerOptions);
  const { dispatch } = planner;

  const seedRef = useRef({
    waypoints: plannerOptions.initialWaypoints,
    options: plannerOptions.initialOptions,
    primaryName: plannerOptions.initialPrimaryName,
    alternateName: plannerOptions.initialAlternateName,
    restoreAlternateKey: plannerOptions.restoreAlternateKey,
  });
  seedRef.current = {
    waypoints: plannerOptions.initialWaypoints,
    options: plannerOptions.initialOptions,
    primaryName: plannerOptions.initialPrimaryName,
    alternateName: plannerOptions.initialAlternateName,
    restoreAlternateKey: plannerOptions.restoreAlternateKey,
  };
  const firstSession = useRef(true);

  useEffect(() => {
    if (firstSession.current) {
      firstSession.current = false;
      return;
    }
    const draftSession = typeof sessionKey === 'string' && sessionKey.includes(':draft-');
    dispatch({
      type: 'RESET',
      waypoints: draftSession ? [] : seedRef.current.waypoints,
      options: seedRef.current.options,
      primaryName: seedRef.current.primaryName,
      alternateName: draftSession ? '' : seedRef.current.alternateName,
      restoreAlternateKey: draftSession ? null : seedRef.current.restoreAlternateKey,
    });
  }, [sessionKey, dispatch]);

  const handleMapClick = useCallback((lngLat: { lng: number; lat: number }) => {
    if (!active) return;
    dispatch({ type: 'ADD_WAYPOINT', longitude: lngLat.lng, latitude: lngLat.lat, source: 'click' });
  }, [active, dispatch]);

  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing = target && (
        target.tagName === 'INPUT'
        || target.tagName === 'TEXTAREA'
        || target.isContentEditable
      );
      if (e.key === 'Escape' && !typing) {
        onExit?.();
        return;
      }
      if (typing) return;
      const mod = e.ctrlKey || e.metaKey;
      if (!mod || e.key.toLowerCase() !== 'z') return;
      e.preventDefault();
      dispatch({ type: e.shiftKey ? 'REDO' : 'UNDO' });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, dispatch, onExit]);

  const flyTo = useCallback((longitude: number, latitude: number, zoom?: number) => {
    const map = getMap?.();
    if (!map) return;
    map.flyTo({ center: [longitude, latitude], zoom: zoom ?? Math.max(map.getZoom(), 15), duration: 700 });
  }, [getMap]);

  const focusWaypoint = useCallback((wp: Waypoint) => flyTo(wp.longitude, wp.latitude), [flyTo]);
  const flyToStep = useCallback((step: ManeuverStep) => {
    if (!Array.isArray(step.location)) return;
    flyTo(step.location[0], step.location[1], 16);
  }, [flyTo]);

  const risk = useRouteRisk(planner.state);
  const assets = useRouteAssets(planner.state);
  const events = useRouteEvents(planner.state);
  const weather = useRouteWeather(planner.state);

  const focusSegment = useCallback((segment: RouteRiskSegment) => {
    flyTo(segment.midpoint[0], segment.midpoint[1], 15);
  }, [flyTo]);

  const focusAsset = useCallback((asset: RouteAsset) => {
    flyTo(asset.longitude, asset.latitude, 16);
  }, [flyTo]);

  const focusEvent = useCallback((event: RouteEvent) => {
    flyTo(event.longitude, event.latitude, 15);
  }, [flyTo]);

  const primaryRoute = planner.state.routes[planner.state.primaryIndex] || null;
  const alternateRoute = planner.state.alternateIndex == null
    ? null
    : planner.state.routes[planner.state.alternateIndex] || null;

  const proximity = useMemo(() => {
    const last = planner.state.waypoints[planner.state.waypoints.length - 1];
    return last ? { longitude: last.longitude, latitude: last.latitude } : null;
  }, [planner.state.waypoints]);

  return {
    ...planner,
    primaryRoute,
    alternateRoute,
    proximity,
    risk,
    assets,
    events,
    weather,
    handleMapClick,
    focusWaypoint,
    focusSegment,
    focusAsset,
    focusEvent,
    flyToStep,
    flyTo,
  };
}
